import React, { useState } from 'react';
import { Button } from './ui/button';
import { Calculator, TrendingDown, ArrowRight, CheckCircle, BarChart3, Zap, MessageCircle } from 'lucide-react';

const ProfitLeakCalculator = () => {
  const [volume, setVolume] = useState('');
  const [rate, setRate] = useState('');
  const [showResult, setShowResult] = useState(false);
  const [showZeroRateMessage, setShowZeroRateMessage] = useState(false);
  const [monthlyLoss, setMonthlyLoss] = useState(0);
  const [yearlyLoss, setYearlyLoss] = useState(0);
  
  const ctaLink = "https://calendly.com/davwaysolution/review";

  const useEstimate = () => {
    setRate('3');
  };

  const calculate = () => {
    const vol = parseFloat(volume) || 50000;
    const r = parseFloat(rate);
    
    // Check if rate is 0 or empty/invalid
    if (r === 0 || (rate !== '' && r === 0)) {
      setShowZeroRateMessage(true);
      setShowResult(false);
      return;
    }
    
    // Use default 3% if no rate entered
    const actualRate = r || 3;
    const monthly = vol * (actualRate / 100);
    const yearly = monthly * 12;
    
    setMonthlyLoss(monthly);
    setYearlyLoss(yearly);
    setShowResult(true);
    setShowZeroRateMessage(false);
  };

  return (
    <section id="calculator" className="py-20" style={{ backgroundColor: '#132A4A' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)', color: '#D4AF37' }}>
            <Calculator size={16} />
            <span>Profit Leak Calculator</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Are You Losing Money on Every Transaction?
          </h2>
          <p className="text-xl text-gray-300">
            Most businesses never realize how much profit is slipping away unnoticed.
          </p>
        </div>

        {/* Calculator Form */}
        <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8">
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Monthly Card Volume
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  value={volume}
                  onChange={(e) => setVolume(e.target.value)}
                  placeholder="50,000"
                  className="w-full pl-8 pr-4 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-gold-500 transition-colors"
                  style={{ fontSize: '18px' }}
                />
              </div>
            </div>
            
            <div>
              <label className="block text-gray-300 text-sm font-medium mb-2">
                Current Processing Rate
              </label>
              <div className="relative">
                <input
                  type="number"
                  value={rate}
                  onChange={(e) => setRate(e.target.value)}
                  placeholder="3"
                  className="w-full pl-4 pr-8 py-4 rounded-lg bg-white/10 border border-white/20 text-white placeholder-gray-400 focus:outline-none focus:border-gold-500 transition-colors"
                  style={{ fontSize: '18px' }}
                />
                <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400">%</span>
              </div>
              <p 
                onClick={useEstimate}
                className="text-sm mt-2 cursor-pointer hover:underline"
                style={{ color: '#F5C542' }}
              >
                Not sure? Use industry average (3%)
              </p>
            </div>
          </div>

          <Button 
            onClick={calculate}
            className="w-full text-white font-bold text-lg py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300"
            style={{ backgroundColor: '#22C55E' }}
          >
            <TrendingDown className="mr-2" size={20} />
            Find My Profit Leak
          </Button>
        </div>

        {/* Zero Rate Message */}
        {showZeroRateMessage && (
          <div 
            className="mt-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 animate-fadeIn"
            style={{ animation: 'fadeIn 0.5s ease-out' }}
          >
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{ backgroundColor: 'rgba(34, 197, 94, 0.2)' }}>
                <CheckCircle style={{ color: '#22C55E' }} size={32} />
              </div>
              <h3 className="text-2xl md:text-3xl font-bold text-white mb-4">
                Great! You're Already at 0%
              </h3>
              <p className="text-xl text-gray-300 mb-6">
                But we offer additional benefits that can still help your business:
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-4 mb-8">
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <BarChart3 style={{ color: '#D4AF37' }} size={28} className="mx-auto mb-2" />
                <p className="text-white font-medium">Daily Reporting</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <Zap style={{ color: '#22C55E' }} size={28} className="mx-auto mb-2" />
                <p className="text-white font-medium">Fast Payments</p>
              </div>
              <div className="bg-white/5 rounded-xl p-4 text-center">
                <MessageCircle style={{ color: '#3B82F6' }} size={28} className="mx-auto mb-2" />
                <p className="text-white font-medium">24/7 Support</p>
              </div>
            </div>
            
            <p className="text-center text-gray-300 mb-8">
              Let's talk to discuss your options and see how we can help improve your payment operations.
            </p>
            
            <div className="text-center">
              <a href={ctaLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="text-white font-bold text-lg px-10 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                  style={{ backgroundColor: '#22C55E' }}
                >
                  Let's Talk
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </a>
            </div>
          </div>
        )}

        {/* Results (for non-zero rates) */}
        {showResult && (
          <div 
            className="mt-8 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-8 animate-fadeIn"
            style={{ animation: 'fadeIn 0.5s ease-out' }}
          >
            <div className="text-center mb-8">
              <h3 className="text-2xl md:text-3xl font-bold mb-2" style={{ color: '#ef4444' }}>
                You may be losing ${monthlyLoss.toLocaleString()} / month
              </h3>
              <h3 className="text-3xl md:text-4xl font-bold" style={{ color: '#ef4444' }}>
                ${yearlyLoss.toLocaleString()} / year
              </h3>
            </div>
            
            <p className="text-gray-300 text-center mb-4">
              This is a hidden profit leak most businesses never see.
            </p>
            
            <p className="text-center mb-8" style={{ color: '#22C55E' }}>
              Our terminals + reporting can eliminate these fees completely and simplify your operations.
            </p>
            
            <div className="text-center">
              <a href={ctaLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  className="text-white font-bold text-lg px-10 py-6 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group"
                  style={{ backgroundColor: '#22C55E' }}
                >
                  Fix My Profit Leak
                  <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
                </Button>
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfitLeakCalculator;
